import React, { FunctionComponent, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiPopupInput from "src/components/core-components/mui/inputs/ByzMuiPopupInput";
import { useAppSelector } from "src/app/hooks";
import { useTranslate } from "src/locales";

export interface OtherCardData {
  operationDate: Date | null;
  operator: string;
  psychologicalStatus: string;
  marriageStatus: string;
  geneticExpertOpinion: string;
}

interface OtherCardListProps {
  onDataChange?: (data: OtherCardData) => void;
  initialData?: OtherCardData | null;
  psychologicalStatusOptions?: Array<{ id: number; name: string }>;
  marriageOptions?: Array<{ id: number; name: string | null }>;
  genExpertOpinionOptions?: Array<{ id: number; name: string; code: string }>;
}

const OtherCardList: FunctionComponent<OtherCardListProps> = ({
  onDataChange,
  initialData,
  psychologicalStatusOptions = [],
  marriageOptions = [],
  genExpertOpinionOptions = [],
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(
    initialData?.operationDate || new Date()
  );
  const [operator, setOperator] = useState<string>(
    initialData?.operator || ""
  );
  const [psychologicalStatus, setPsychologicalStatus] = useState<string>(
    initialData?.psychologicalStatus || ""
  );
  const [marriageStatus, setMarriageStatus] = useState<string>(
    initialData?.marriageStatus || ""
  );
  const [geneticExpertOpinion, setGeneticExpertOpinion] = useState<string>(
    initialData?.geneticExpertOpinion || ""
  );

  useEffect(() => {
    if (initialData) {
      setOperationDate(initialData.operationDate || new Date());
      setOperator(initialData.operator || "");
      setPsychologicalStatus(initialData.psychologicalStatus || "");
      setMarriageStatus(initialData.marriageStatus || "");
      setGeneticExpertOpinion(initialData.geneticExpertOpinion || "");
    } else {
      // Veri yoksa sadece tarihi set et, kullanıcı adını set etme
      setOperationDate(new Date());
      setOperator("");
      setPsychologicalStatus("");
      setMarriageStatus("");
      setGeneticExpertOpinion("");
    }
  }, [initialData]);

  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        operationDate,
        operator,
        psychologicalStatus,
        marriageStatus,
        geneticExpertOpinion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationDate, operator, psychologicalStatus, marriageStatus, geneticExpertOpinion]);

  return (
    <Box sx={{ width: "100%", position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "22px", textAlign: "left", fontSize: "14px", color: "#111827", fontFamily: "Urbanist" }}>
      <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>{t("other.operation-date")} :</Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <ByzMuiDateTimePicker
                value={operationDate}
                onChange={(val) => setOperationDate(val as Date | null)}
                width="180px"
                disabled
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>{t("other.operation-user")} :</Box>
            <ByzMuiTextField
              label={t("other.operation-user")}
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              sx={{ width: "180px" }}
              cantChange
              noPlaceholder
            />
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>{t("general-health-other.psychological-status")} :</Box>
            <ByzMuiPopupInput
              label={t("general-health-other.psychological-status")}
              value={psychologicalStatus}
              onChange={(val: string) => setPsychologicalStatus(val === "" ? "" : val)}
              dialogTitle={t("general-health-other.psychological-status")}
              options={psychologicalStatusOptions.map((option) => ({
                value: option.id.toString(),
                label: option.name,
              }))}
              sx={{ width: "180px" }}
            />
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>{t("general-health-other.marriage-status")} :</Box>
            <ByzMuiPopupInput
              label={t("general-health-other.marriage-status")}
              value={marriageStatus}
              onChange={(val: string) => setMarriageStatus(val === "" ? "" : val)}
              dialogTitle={t("general-health-other.marriage-status")}
              options={marriageOptions.map((option) => ({
                value: option.id.toString(),
                label: option.name || "",
              }))}
              sx={{ width: "180px" }}
            />
          </Box>
        </Box>

        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "20px" }}>
            <Box sx={{ width: "200px", position: "relative", lineHeight: "150%", fontWeight: 600 }}>{t("general-health-other.genetic-expert-opinion")} :</Box>
            <ByzMuiPopupInput
              label={t("general-health-other.genetic-expert-opinion")}
              value={geneticExpertOpinion}
              onChange={(val: string) => setGeneticExpertOpinion(val === "" ? "" : val)}
              dialogTitle={t("general-health-other.genetic-expert-opinion")}
              options={genExpertOpinionOptions.map((option) => ({
                value: option.id.toString(),
                label: option.name,
              }))}
              sx={{ width: "180px" }}
            />
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default OtherCardList;
